// ─────────────────────────────────────────────────────────────────────────────
// MessageBubble – Renders a single chat message with tool call/result display
// ESG-only. Includes link allowlist + transparency disclosure.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import type { Message, ToolCallResult } from "../types";
import { safeLinkHref } from "../security";
import ForecastChart, { type ForecastChartData } from "./ForecastChart";

// Extract the chart-data envelope embedded by tools and return [chartPayload, cleanText].
const CHART_RE = /<<<chart-data:(\{.*?\})>>>/;
function extractChartPayload(
	text: string
): { chart: ForecastChartData | null; cleanText: string } {
	if (!text || !text.includes("<<<chart-data:")) return { chart: null, cleanText: text };
	const m = text.match(CHART_RE);
	if (!m) return { chart: null, cleanText: text };
	let chart: ForecastChartData | null = null;
	try {
		const parsed = JSON.parse(m[1]);
		if (parsed && parsed.type === "forecast") chart = parsed as ForecastChartData;
	} catch {
		/* ignore malformed envelope */
	}
	const cleanText = text.replace(CHART_RE, "").trim();
	return { chart, cleanText };
}

/** Strip any chart-data marker that might have leaked into the assistant text. */
function stripChartEnvelopes(text: string): string {
	if (!text || !text.includes("<<<chart-data:")) return text;
	return text.replace(/<<<chart-data:\{.*?\}>>>/g, "").trim();
}

/** Best guess at what the assistant is currently doing, for the loader label. */
function activeToolName(message: Message): string | undefined {
	const calls = message.toolCalls ?? [];
	const last = calls[calls.length - 1];
	return last?.name;
}

const TOOL_LOADER_LABELS: Record<string, string> = {
	web_search: "Searching the web",
	parallel_search: "Running parallel searches",
	esg_lookup: "Looking up the framework",
	platform_help: "Checking platform docs",
	read_page_context: "Reading the page",
	read_chart_data: "Reading the chart data",
	analyze_dashboard: "Scanning the dashboard",
	forecast_series: "Forecasting the series",
};

function ThinkingIndicator({ activeTool }: { activeTool?: string }) {
	const label = activeTool
		? TOOL_LOADER_LABELS[activeTool] ?? "Thinking"
		: "Thinking";
	return (
		<div className="cai-thinking" aria-live="polite" aria-label={label}>
			<div className="cai-thinking-orb" aria-hidden="true">
				<div className="cai-thinking-orb-core" />
				<div className="cai-thinking-orb-ring" />
				<div className="cai-thinking-orb-spark" />
			</div>
			<div className="cai-thinking-text">
				<span>{label}</span>
				<span className="cai-thinking-ellipsis">
					<span>.</span>
					<span>.</span>
					<span>.</span>
				</span>
			</div>
		</div>
	);
}

// ── Simple Markdown renderer (no external deps) ───────────────────────────────

function renderMarkdown(text: string): React.ReactNode[] {
	const lines = text.split("\n");
	const elements: React.ReactNode[] = [];
	let i = 0;
	let keyCounter = 0;
	const key = () => `md-${keyCounter++}`;

	while (i < lines.length) {
		const line = lines[i];

		// Headings — match #..###### with explicit precedence (longest first)
		const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line);
		if (headingMatch) {
			const level = headingMatch[1].length;
			const content = headingMatch[2];
			const headingStyles: Record<number, React.CSSProperties> = {
				1: {
					margin: "14px 0 6px",
					fontSize: "16px",
					fontWeight: 800,
					color: "#0f172a",
					letterSpacing: "-0.02em",
				},
				2: {
					margin: "12px 0 6px",
					fontSize: "15px",
					fontWeight: 700,
					color: "#0f172a",
					letterSpacing: "-0.01em",
				},
				3: {
					margin: "10px 0 4px",
					fontSize: "14px",
					fontWeight: 700,
					color: "#1E1730",
					letterSpacing: "-0.01em",
				},
				4: {
					margin: "10px 0 4px",
					fontSize: "13px",
					fontWeight: 700,
					color: "#1E1730",
				},
				5: {
					margin: "8px 0 3px",
					fontSize: "12.5px",
					fontWeight: 700,
					color: "#3F3552",
					textTransform: "uppercase",
					letterSpacing: "0.04em",
				},
				6: {
					margin: "8px 0 3px",
					fontSize: "11.5px",
					fontWeight: 700,
					color: "#6B7280",
					textTransform: "uppercase",
					letterSpacing: "0.06em",
				},
			};
			const Tag = (`h${Math.min(level + 1, 6)}` as unknown) as keyof JSX.IntrinsicElements;
			elements.push(
				<Tag key={key()} style={headingStyles[level]}>
					{inlineFormat(content)}
				</Tag>
			);
		} else if (line.startsWith("```")) {
			// Fenced code block
			const fenceLang = line.slice(3).trim();
			i++;
			const codeLines: string[] = [];
			while (i < lines.length && !lines[i].startsWith("```")) {
				codeLines.push(lines[i]);
				i++;
			}
			i++; // skip closing fence
			elements.push(
				<pre
					key={key()}
					style={{
						margin: "8px 0",
						padding: "10px 12px",
						background: "#1E1730",
						color: "#F5F3F8",
						borderRadius: 10,
						fontSize: 11.5,
						lineHeight: 1.55,
						fontFamily:
							"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
						overflowX: "auto",
						whiteSpace: "pre",
					}}
					className="cai-scroll"
				>
					{fenceLang && (
						<div
							style={{
								fontSize: 9.5,
								textTransform: "uppercase",
								letterSpacing: "0.08em",
								color: "rgba(245,243,248,0.5)",
								marginBottom: 6,
								fontFamily: "inherit",
							}}
						>
							{fenceLang}
						</div>
					)}
					<code style={{ fontFamily: "inherit", color: "inherit" }}>
						{codeLines.join("\n")}
					</code>
				</pre>
			);
			continue;
		} else if (line.startsWith("> ")) {
			elements.push(
				<blockquote
					key={key()}
					style={{
						margin: "8px 0",
						padding: "8px 12px",
						borderLeft: "3px solid #B31F7E",
						background:
							"linear-gradient(135deg, rgba(72,42,122,0.04), rgba(179,31,126,0.04))",
						borderRadius: "0 8px 8px 0",
						color: "#3F3552",
						fontSize: "12.5px",
						fontStyle: "italic",
					}}
				>
					{inlineFormat(line.slice(2))}
				</blockquote>
			);
		} else if (line.startsWith("- ") || line.startsWith("* ")) {
			const listItems: React.ReactNode[] = [];
			while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) {
				listItems.push(
					<li key={key()} style={{ marginBottom: 3 }}>
						{inlineFormat(lines[i].slice(2))}
					</li>
				);
				i++;
			}
			elements.push(
				<ul key={key()} style={{ margin: "4px 0", paddingLeft: 20 }}>
					{listItems}
				</ul>
			);
			continue;
		} else if (/^\d+\.\s/.test(line)) {
			const listItems: React.ReactNode[] = [];
			while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
				const content = lines[i].replace(/^\d+\.\s/, "");
				listItems.push(
					<li key={key()} style={{ marginBottom: 3 }}>
						{inlineFormat(content)}
					</li>
				);
				i++;
			}
			elements.push(
				<ol key={key()} style={{ margin: "4px 0", paddingLeft: 20 }}>
					{listItems}
				</ol>
			);
			continue;
		} else if (line === "---" || line === "***") {
			elements.push(
				<hr
					key={key()}
					style={{ border: "none", borderTop: "1px solid #E5DEF2", margin: "10px 0" }}
				/>
			);
		} else if (isTableHeaderRow(line, lines[i + 1])) {
			// Markdown table:  | h1 | h2 |     ← line
			//                  | -- | -- |    ← lines[i+1] (separator)
			//                  | v1 | v2 |    ← data rows
			const headers = parseTableRow(line);
			i += 2; // skip header + separator
			const rows: string[][] = [];
			while (i < lines.length && lines[i].includes("|")) {
				const cells = parseTableRow(lines[i]);
				if (cells.length === 0) break;
				rows.push(cells);
				i++;
			}
			elements.push(renderMarkdownTable(headers, rows, key()));
			continue;
		} else if (!line.trim()) {
			elements.push(<div key={key()} style={{ height: 6 }} />);
		} else {
			elements.push(
				<p key={key()} style={{ margin: "3px 0", lineHeight: 1.6 }}>
					{inlineFormat(line)}
				</p>
			);
		}

		i++;
	}

	return elements;
}

// ── Markdown table support ───────────────────────────────────────────────────

function parseTableRow(line: string): string[] {
	if (!line || !line.includes("|")) return [];
	let s = line.trim();
	if (s.startsWith("|")) s = s.slice(1);
	if (s.endsWith("|")) s = s.slice(0, -1);
	return s.split("|").map((c) => c.trim());
}

function isTableHeaderRow(line: string, next?: string): boolean {
	if (!line || !next) return false;
	if (!line.includes("|") || !next.includes("|")) return false;
	const sep = next.replace(/\s/g, "");
	// Separator: only |, -, :, or whitespace, with at least one dash
	return /^\|?[-:|]+\|?$/.test(sep) && sep.includes("-");
}

function renderMarkdownTable(
	headers: string[],
	rows: string[][],
	keyStr: string
): React.ReactNode {
	return (
		<div
			key={keyStr}
			style={{
				margin: "8px 0",
				borderRadius: 10,
				border: "1px solid #EAE3F4",
				overflow: "hidden",
				background: "#fff",
			}}
		>
			<div
				className="cai-scroll"
				style={{ overflowX: "auto" }}
			>
				<table
					style={{
						borderCollapse: "collapse",
						width: "100%",
						fontSize: 12,
						lineHeight: 1.5,
					}}
				>
					<thead>
						<tr
							style={{
								background:
									"linear-gradient(135deg, rgba(72,42,122,0.06), rgba(179,31,126,0.06))",
							}}
						>
							{headers.map((h, idx) => (
								<th
									key={idx}
									style={{
										padding: "8px 10px",
										textAlign: "left",
										fontWeight: 700,
										color: "#482A7A",
										borderBottom: "1px solid #EAE3F4",
										fontSize: 11,
										textTransform: "uppercase",
										letterSpacing: "0.04em",
										whiteSpace: "nowrap",
									}}
								>
									{inlineFormat(h)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rIdx) => (
							<tr
								key={rIdx}
								style={{
									background: rIdx % 2 === 0 ? "#fff" : "#FBFAFD",
								}}
							>
								{row.map((cell, cIdx) => (
									<td
										key={cIdx}
										style={{
											padding: "7px 10px",
											borderTop: rIdx === 0 ? "none" : "1px solid #F3EEFA",
											color: "#1E1730",
											verticalAlign: "top",
										}}
									>
										{inlineFormat(cell)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function inlineFormat(text: string): React.ReactNode {
	if (!text) return null;
	const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
	return (
		<>
			{parts.map((part, i) => {
				if (!part) return null;
				if (part.startsWith("**") && part.endsWith("**")) {
					return (
						<strong key={i} style={{ color: "#1E1730" }}>
							{part.slice(2, -2)}
						</strong>
					);
				}
				if (part.startsWith("`") && part.endsWith("`")) {
					return (
						<code
							key={i}
							style={{
								background: "#F5F3F8",
								padding: "1px 5px",
								borderRadius: 4,
								fontFamily:
									"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
								fontSize: "12px",
								color: "#482A7A",
								border: "1px solid #E5DEF2",
							}}
						>
							{part.slice(1, -1)}
						</code>
					);
				}
				const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
				if (linkMatch) {
					const safe = safeLinkHref(linkMatch[2]);
					if (!safe) {
						// Block unsafe schemes (javascript:, data:, vbscript:) — render as text
						return (
							<span
								key={i}
								title="Blocked link (unsafe URL scheme)"
								style={{ color: "#9ca3af", textDecoration: "line-through" }}
							>
								{linkMatch[1]}
							</span>
						);
					}
					return (
						<a
							key={i}
							href={safe}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								color: "#482A7A",
								textDecoration: "underline",
								textDecorationColor: "#D4C8E6",
								textUnderlineOffset: "2px",
							}}
						>
							{linkMatch[1]}
						</a>
					);
				}
				return part;
			})}
		</>
	);
}

// ── Tool Call Display ─────────────────────────────────────────────────────────

const TOOL_ICONS: Record<string, string> = {
	web_search: "🔍",
	parallel_search: "🔄",
	esg_lookup: "🌱",
	read_page_context: "📄",
	read_chart_data: "📊",
	platform_help: "💡",
	analyze_dashboard: "✨",
	forecast_series: "📈",
};

const TOOL_LABELS: Record<string, string> = {
	web_search: "Web Search",
	parallel_search: "Parallel Search",
	esg_lookup: "ESG Frameworks",
	read_page_context: "Page Context",
	read_chart_data: "Read Chart",
	platform_help: "Platform Help",
	analyze_dashboard: "Auto-Insights",
	forecast_series: "Forecast",
};

interface ToolCallCardProps {
	toolName: string;
	input: Record<string, unknown>;
	result?: ToolCallResult;
}

function ToolCallCard({ toolName, input, result }: ToolCallCardProps) {
	const [expanded, setExpanded] = React.useState(false);
	const icon = TOOL_ICONS[toolName] ?? "🔧";
	const label = TOOL_LABELS[toolName] ?? toolName;
	const inputSummary = input.query
		? String(input.query)
		: input.queries
		? (input.queries as string[]).join(", ")
		: input.chart_id
		? `chart: ${String(input.chart_id)}`
		: input.chart_title
		? `"${String(input.chart_title)}"`
		: input.topic
		? String(input.topic)
		: JSON.stringify(input).slice(0, 60);

	// Extract any embedded chart-data envelope from the result content so we can
	// render a real SVG chart instead of letting Claude scribble ASCII.
	const resultStr = result ? String(result.content) : "";
	const { chart, cleanText } = extractChartPayload(resultStr);

	return (
		<div
			className="cai-tool-card"
			style={{
				background: "#FBFAFD",
				border: "1px solid #EAE3F4",
				borderRadius: 10,
				marginTop: 6,
				fontSize: 12,
				overflow: "hidden",
			}}
		>
			<button
				onClick={() => setExpanded((e) => !e)}
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					gap: 8,
					padding: "8px 12px",
					background: "transparent",
					border: "none",
					cursor: "pointer",
					textAlign: "left",
					color: "#475569",
				}}
			>
				<span style={{ fontSize: 14 }}>{icon}</span>
				<span style={{ fontWeight: 600, color: "#1E1730" }}>{label}</span>
				<span
					style={{
						color: "#94a3b8",
						flex: 1,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontWeight: 400,
					}}
				>
					{inputSummary}
				</span>
				<span
					style={{
						fontSize: 10,
						fontWeight: 600,
						padding: "2px 8px",
						borderRadius: 999,
						background: result
							? result.isError
								? "#FEE2E2"
								: "#DCFCE7"
							: "#FEF3C7",
						color: result ? (result.isError ? "#B91C1C" : "#15803D") : "#92400E",
					}}
				>
					{result ? (result.isError ? "Error" : "Done") : "Running"}
				</span>
				<span style={{ color: "#94a3b8", fontSize: 10 }}>{expanded ? "▲" : "▼"}</span>
			</button>

			{/* Inline chart rendered from embedded chart-data envelope. Always
			    visible (not hidden behind expand toggle) because the chart is
			    the whole point of the answer. */}
			{chart && (
				<div style={{ padding: "0 8px 8px" }}>
					<ForecastChart data={chart} />
				</div>
			)}

			{expanded && result && (
				<div
					style={{
						padding: "10px 12px",
						borderTop: "1px solid #EAE3F4",
						maxHeight: 320,
						overflowY: "auto",
						color: result.isError ? "#dc2626" : "#334155",
						fontSize: 11.5,
						lineHeight: 1.55,
						whiteSpace: "pre-wrap",
						fontFamily:
							"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
						background: "#FDFCFE",
					}}
					className="cai-scroll"
				>
					{cleanText.slice(0, 3000)}
					{cleanText.length > 3000 && (
						<span style={{ color: "#94a3b8" }}>{"\n…(truncated)"}</span>
					)}
				</div>
			)}
		</div>
	);
}

// ── Main MessageBubble ────────────────────────────────────────────────────────

interface MessageBubbleProps {
	message: Message;
}

export default function MessageBubble({ message }: MessageBubbleProps) {
	const isUser = message.role === "user";
	const isAssistant = message.role === "assistant";

	const isTypingPlaceholder = isAssistant && message.isStreaming && !message.content;

	const userBubble: React.CSSProperties = {
		maxWidth: "88%",
		padding: "11px 14px",
		borderRadius: "18px 18px 4px 18px",
		fontSize: 13,
		lineHeight: 1.55,
		wordBreak: "break-word",
		background:
			"linear-gradient(135deg, #482A7A 0%, #6B2A8E 60%, #B31F7E 100%)",
		color: "#fff",
		alignSelf: "flex-end",
		boxShadow:
			"0 6px 16px rgba(72, 42, 122, 0.28), 0 0 0 1px rgba(255,255,255,0.06) inset",
	};

	const assistantBubble: React.CSSProperties = isTypingPlaceholder
		? {
				// Inline pill while thinking — wraps just the loader, never a
				// full-width white rectangle.
				maxWidth: "fit-content",
				width: "auto",
				padding: "9px 14px",
				borderRadius: "4px 16px 16px 16px",
				background:
					"linear-gradient(135deg, #FAF7FF 0%, #FFFFFF 60%, #FDF7FB 100%)",
				color: "#1E1730",
				alignSelf: "flex-start",
				border: "1px solid #EAE3F4",
				boxShadow:
					"0 2px 6px rgba(72, 42, 122, 0.10), 0 0 0 1px rgba(255,255,255,0.5) inset",
		  }
		: {
				// Assistant bubbles need to stretch wider — they hold tables, code,
				// and structured content that gets squished at narrower widths.
				maxWidth: "98%",
				width: "98%",
				padding: "12px 16px",
				borderRadius: "4px 18px 18px 18px",
				fontSize: 13,
				lineHeight: 1.6,
				wordBreak: "break-word",
				background: "#fff",
				color: "#1E1730",
				alignSelf: "flex-start",
				border: "1px solid #EEE7F6",
				boxShadow:
					"0 1px 3px rgba(72, 42, 122, 0.06), 0 4px 12px rgba(72, 42, 122, 0.04)",
		  };

	return (
		<div
			className="cai-msg-enter"
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: isUser ? "flex-end" : "flex-start",
				marginBottom: 14,
				gap: 4,
			}}
		>
			{/* Role + timestamp */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 6,
					fontSize: 10.5,
					color: "#94a3b8",
					fontWeight: 500,
				}}
			>
				{isAssistant && (
					<span
						style={{
							width: 18,
							height: 18,
							borderRadius: "50%",
							background:
								"linear-gradient(135deg, #482A7A, #B31F7E)",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 10,
							color: "#fff",
						}}
					>
						✦
					</span>
				)}
				<span style={{ color: "#64748b" }}>
					{isUser ? "You" : "ESG Assistant"}
				</span>
				<span style={{ fontSize: 9.5, color: "#cbd5e1" }}>
					{new Date(message.timestamp).toLocaleTimeString([], {
						hour: "2-digit",
						minute: "2-digit",
					})}
				</span>
			</div>

			{/* Message bubble */}
			<div style={isUser ? userBubble : assistantBubble}>
				{isUser ? (
					<span>{message.content}</span>
				) : (
					<div>
						{message.isStreaming && !message.content ? (
							<ThinkingIndicator activeTool={activeToolName(message)} />
						) : (
							renderMarkdown(stripChartEnvelopes(message.content))
						)}
					</div>
				)}
			</div>

			{/* Tool call cards */}
			{isAssistant && message.toolCalls && message.toolCalls.length > 0 && (
				<div style={{ width: "98%", maxWidth: "98%" }}>
					{message.toolCalls.map((call) => {
						const result = message.toolResults?.find(
							(r) => r.toolCallId === call.id
						);
						return (
							<ToolCallCard
								key={call.id}
								toolName={call.name}
								input={call.input}
								result={result}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}
